import { PropertySource } from "@/api";
import type {
    IExtractedFieldResponse,
    IExtractedPropertyValue,
} from "@/api/documents/documents.types";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { getPropertyDisplayValue } from "@/utils/fields";
import { Chip, Stack, Typography } from "@mui/material";
import { PageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { useCallback, useRef, useState } from "react";
import { Check, PencilLine, Trash2 } from "lucide-react";
import { EditablePropertyRow } from "./EditablePropertyRow";
import { AddPropertyInlineForm } from "./AddPropertyInlineForm";
import { FieldGroup } from "./FieldGroup";

interface IProps {
    field: IExtractedFieldResponse;
    pageNavigationPluginInstance: PageNavigationPlugin;
    isEditable?: boolean;
    onSave?: (
        propertyId: number,
        value: string | null,
        pageNumber: number | null
    ) => void;
    onDelete?: (propertyId: number) => void;
    /** Unified save handler with source-based dispatch (preferred over onSave). */
    onPropertySave?: (
        propertyId: number,
        value: string | null,
        pageNumber: number | null,
        source: PropertySource
    ) => void;
    /** Unified delete handler with source-based dispatch (preferred over onDelete). */
    onPropertyDelete?: (propertyId: number, source: PropertySource) => void;
    /** When set, this field is an adapted custom field that supports delete-field UI. */
    customFieldId?: number;
    onDeleteField?: () => void;
    /** Add property handler for both custom fields and extracted fields (bridge-or-add). */
    onAddProperty?: (data: {
        customFieldId: number;
        name: string;
        value: string | null;
        pageNumber: number | null;
    }) => void;
    currentPage?: number;
    totalPages?: number;
    isAddingProperty?: boolean;
}

const resolveSource = (property: IExtractedPropertyValue): PropertySource =>
    property.source === "custom" ? "custom" : "extracted";

export const DynamicScalarField = ({
    field,
    pageNavigationPluginInstance,
    isEditable = false,
    onSave,
    onDelete,
    onPropertySave,
    onPropertyDelete,
    customFieldId,
    onDeleteField,
    onAddProperty,
    currentPage = 1,
    totalPages = 1,
    isAddingProperty = false,
}: IProps) => {
    const item = field.items[0];
    const properties = item?.properties || [];

    const makeSaveHandler = (property: IExtractedPropertyValue) => {
        return (
            propertyId: number,
            value: string | null,
            pageNumber: number | null
        ) => {
            if (onPropertySave) {
                onPropertySave(
                    propertyId,
                    value,
                    pageNumber,
                    resolveSource(property)
                );
            } else {
                onSave?.(propertyId, value, pageNumber);
            }
        };
    };

    const makeDeleteHandler = (property: IExtractedPropertyValue) => {
        return (propertyId: number) => {
            if (onPropertyDelete) {
                onPropertyDelete(propertyId, resolveSource(property));
            } else {
                onDelete?.(propertyId);
            }
        };
    };

    return (
        <FieldGroup
            fieldName={field.field_name}
            headerAction={
                customFieldId && isEditable && onDeleteField ? (
                    <DeleteFieldButton
                        fieldName={field.field_name}
                        onDeleteField={onDeleteField}
                    />
                ) : undefined
            }
        >
            {properties.length === 0 ? (
                <span className="text-muted-foreground/60 py-0.5 text-[13px] italic">
                    Not found
                </span>
            ) : (
                <div className={cn(isEditable ? "space-y-0" : "space-y-2")}>
                    {properties.map((property) => {
                        const source = property.source ?? "extracted";
                        const key = `${source}-${property.id}`;

                        return isEditable ? (
                            <EditablePropertyRow
                                key={key}
                                property={property}
                                isEditable={isEditable}
                                onSave={makeSaveHandler(property)}
                                onDelete={makeDeleteHandler(property)}
                                pageNavigationPluginInstance={
                                    pageNavigationPluginInstance
                                }
                                totalPages={totalPages}
                                showEditedBadge={source !== "custom"}
                            />
                        ) : (
                            <PropertyRow
                                key={key}
                                property={property}
                                pageNavigationPluginInstance={
                                    pageNavigationPluginInstance
                                }
                            />
                        );
                    })}
                </div>
            )}
            {isEditable && onAddProperty && (
                <AddPropertyInlineForm
                    customFieldId={customFieldId}
                    isEditable={isEditable}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    isPending={isAddingProperty}
                    onSubmit={onAddProperty}
                />
            )}
        </FieldGroup>
    );
};

// Read-only property row
interface IPropertyRowProps {
    property: IExtractedPropertyValue;
    pageNavigationPluginInstance: PageNavigationPlugin;
}

const PropertyRow = ({
    property,
    pageNavigationPluginInstance,
}: IPropertyRowProps) => {
    const handleChipClick = useCallback(() => {
        if (!property.page_number) return;
        pageNavigationPluginInstance?.jumpToPage(property.page_number - 1);
    }, [property.page_number, pageNavigationPluginInstance]);

    const isCustom = property.source === "custom";

    return (
        <Stack
            direction="row"
            gap={1}
            sx={{
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
            }}
        >
            <div className="flex items-center gap-1">
                <Typography variant="body2">
                    {property.property_name}
                </Typography>
                {isCustom && (
                    <PencilLine
                        size={10}
                        className="text-muted-foreground/40 shrink-0"
                        aria-label="User-added property"
                    />
                )}
            </div>
            <Stack direction="row" gap={1.5} sx={{ alignItems: "center" }}>
                <Typography variant="body2" color="text.secondary">
                    {getPropertyDisplayValue(property.value ?? undefined)}
                </Typography>
                {property.page_number && (
                    <Chip
                        label={`p. ${property.page_number}`}
                        onClick={handleChipClick}
                        size="small"
                        color="tertiary"
                        sx={{ height: 20, fontSize: "0.75rem" }}
                    />
                )}
            </Stack>
        </Stack>
    );
};

// Two-click confirm delete button for custom fields
function DeleteFieldButton({
    fieldName,
    onDeleteField,
}: {
    fieldName: string;
    onDeleteField: () => void;
}) {
    const [isConfirming, setIsConfirming] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            if (!isConfirming) {
                setIsConfirming(true);
                timerRef.current = setTimeout(
                    () => setIsConfirming(false),
                    2000
                );
            } else {
                if (timerRef.current) clearTimeout(timerRef.current);
                setIsConfirming(false);
                onDeleteField();
            }
        },
        [isConfirming, onDeleteField]
    );

    return (
        <Button
            variant={isConfirming ? "destructive" : "ghost"}
            size="icon-xs"
            onClick={handleClick}
            onMouseLeave={() => {
                if (isConfirming) {
                    if (timerRef.current) clearTimeout(timerRef.current);
                    setIsConfirming(false);
                }
            }}
            className={cn(
                "shrink-0 transition-opacity duration-150",
                isConfirming ? "opacity-100" : "opacity-60 hover:opacity-100"
            )}
            aria-label={
                isConfirming
                    ? `Confirm delete ${fieldName}`
                    : `Delete ${fieldName}`
            }
        >
            {isConfirming ? (
                <Check size={12} aria-hidden />
            ) : (
                <Trash2 size={12} aria-hidden />
            )}
        </Button>
    );
}
